export const privacyPolicyBodyEn = `Last updated: 3 October 2026

This notice describes how the personal data of users who use the pro20.net online portal to make reservations, place orders, request information, and use the other services provided are processed.

## 1. Data controller

The controller of the personal data is:

Mada Srl

Trade name: Mada Srl

Registered office: Via 4 novembre 12 - Cisternino (BR) 72014

VAT / tax code: 02750160745

Email: {{email}}

PEC: -

For any request relating to the protection of personal data, you can contact the controller using the details above.

If a Data Protection Officer (DPO) has been appointed, you can contact them at:

{{email}}

## 2. Types of data processed

When you use the portal, the following data may be collected and processed, depending on the services used:

- first and last name;
- email address;
- telephone number and dialling code;
- reservation information;
- number of people associated with the reservation;
- date and time of the reservation;
- information and requests entered voluntarily by the user;
- history of reservations and visits;
- information about orders placed;
- products and services purchased;
- information about delivery or collection of orders;
- information about transactions and payment status;
- any promotional codes, coupons, points, or loyalty programmes;
- preferences and consents given by the user;
- technical data needed for the operation and security of the portal.

Please do not enter unnecessary information in notes or free-text requests, in particular personal data belonging to special categories under Article 9 GDPR, unless it is strictly necessary to handle the request.

## 3. Purposes and legal bases of processing

### 3.1 Reservations

Personal data are processed in order to:

- receive and manage a reservation request;
- confirm, change, or cancel a reservation;
- contact the customer about the reservation;
- manage arrival and attendance at the restaurant;
- manage any requests linked to the reservation.

The legal basis is the performance of a contract or of pre-contractual measures requested by the data subject under Article 6(1)(b) GDPR.

## 4. Orders, takeaway, and delivery

Where the portal allows orders to be placed, personal data may be used to:

- receive and manage the order;
- prepare the products ordered;
- manage collection at the venue;
- arrange delivery where applicable;
- send updates on the status of the order;
- manage changes, cancellations, or refunds;
- provide customer support.

The legal basis is the performance of the contract or of pre-contractual measures requested by the data subject.

## 5. Payments

Where provided, the portal may allow payment of orders, deposits, advances, services, or other amounts through specialised payment service providers.

The data needed for the transaction may be sent directly to the payment service provider.

The restaurant and the portal may receive information about the outcome of the transaction, such as:

- payment completed;
- payment declined;
- card authorisation;
- deposit made;
- refund;
- transaction identifier.

Full payment-card details are not stored by the restaurant when payment is handled directly by the payment provider.

The legal basis is the performance of the contract and, where applicable, compliance with a legal obligation.

## 6. Service communications

The contact details provided by the user may be used to send communications strictly connected to the requested service, for example:

- reservation confirmation;
- request for confirmation;
- change or cancellation of the reservation;
- reservation reminder;
- order confirmation;
- update on the status of the order;
- communications about payment;
- information needed to perform the service correctly.

These communications may be sent, depending on the contact details provided and the services enabled by the restaurant, by email, SMS, WhatsApp, or other messaging systems.

When they are strictly necessary to manage the requested service, these communications are not promotional.

## 7. Marketing and promotional communications

Only with the user's consent, contact data may be used to send commercial and promotional communications about:

- offers and promotions;
- events;
- new menus or products;
- restaurant initiatives;
- coupons and discount codes;
- loyalty programmes;
- personalised commercial communications.

Communications may be sent through the channels for which the user has given consent, such as email, SMS, WhatsApp, or other means permitted by applicable law.

Consent for marketing purposes is optional.

Refusing consent does not prevent you from making reservations, placing orders, or using the portal's other main services.

Consent may be withdrawn at any time, without affecting the lawfulness of processing carried out before withdrawal.

## 8. Profiling and personalisation

Only where provided, and with consent when required by law, the controller may use information about the user's interactions with the restaurant to analyse interests, habits, and preferences.

By way of example, this may include:

- frequency of reservations;
- order history;
- products purchased;
- use of promotions and coupons;
- participation in loyalty programmes;
- preferences expressed by the user;
- interactions with promotional communications.

This information may be used to create customer segments and make offers, promotions, and communications more relevant.

Consent to profiling, when required, is optional and may be withdrawn at any time.

Refusing consent does not prevent use of the ordinary reservation and ordering services.

## 9. Loyalty programmes, promotions, and coupons

Where the restaurant offers loyalty programmes, promotions, points schemes, or coupons, the user's data may be processed in order to:

- award points or benefits;
- check the requirements for a promotion;
- generate coupons;
- check the use of coupons;
- manage rewards and benefits;
- prevent fraudulent use of promotions.

The specific conditions of the programme or promotion may be set out in separate rules.

## 10. Automations

The restaurant may use automated systems to manage some activities linked to the customer relationship.

For example, the system may automatically:

- send a reminder before a reservation;
- send a communication after a visit;
- update the status of a reservation or an order;
- award points or coupons;
- send communications when certain conditions occur.

When these automations have a promotional purpose or involve profiling, the relevant legal bases apply and, where necessary, the user's consent is requested in advance.

## 11. Provision of data

Providing data marked as mandatory is necessary in order to supply the requested service.

For example, if the data needed for a reservation are not provided, it may be impossible to complete the reservation.

Providing data and consent for marketing and profiling is optional and does not affect access to the main services.

## 12. Recipients of the data

Personal data may be processed by staff authorised by the controller and by suppliers that provide services needed to run the business and the platform.

By way of example, these parties may include:

- providers of the software platform;
- hosting and cloud providers;
- email service providers;
- SMS service providers;
- messaging service providers;
- payment service providers;
- delivery service providers;
- technical, administrative, or tax advisers;
- other parties that process data on behalf of the controller.

Where required by law, these parties act as processors under Article 28 GDPR.

Data may also be disclosed to the competent authorities where required by a legal obligation.

## 13. Transfers of data outside the European Economic Area

Some technology providers used by the controller may process personal data outside the European Economic Area.

In those cases, processing and transfer will comply with the GDPR, using the applicable tools and safeguards, such as adequacy decisions or Standard Contractual Clauses approved by the European Commission, where required.

## 14. Retention period

Personal data are kept for as long as needed to achieve the purposes for which they were collected and afterwards for any periods required by applicable law.

In particular:

- data relating to reservations and orders are kept for the period needed to manage the customer relationship and any administrative, accounting, or legal obligations;
- data relating to transactions are kept in line with applicable tax and accounting obligations;
- data used for marketing are kept until consent is withdrawn, or for another period set by the controller according to the principles of necessity and proportionality;
- data used for profiling are kept for the period defined by the controller in relation to the purposes pursued and are then deleted or anonymised.

The specific retention periods adopted by the controller can be requested using the contact details in this notice.

## 15. Data security

The controller and the suppliers involved adopt technical and organisational measures appropriate to the risk in order to protect personal data from loss, destruction, unauthorised access, disclosure, alteration, or unlawful processing.

## 16. Rights of the data subject

Where provided by the GDPR, the data subject may exercise their rights and in particular request:

- access to their personal data;
- rectification of inaccurate data;
- erasure of data;
- restriction of processing;
- data portability;
- objection to processing;
- withdrawal of consent previously given.

Withdrawal of consent does not affect the lawfulness of processing carried out before withdrawal.

Requests may be sent to:

{{email}}

The data subject also has the right to lodge a complaint with the Italian Data Protection Authority (Garante per la protezione dei dati personali) in the manner provided by applicable law.

## 17. Cookies and similar technologies

The portal may use cookies and other technologies needed for it to work.

Any use of analytics cookies, profiling cookies, or third-party cookies is governed by the specific Cookie Policy available on the portal and, where necessary, through the consent management system.

## 18. Changes to this notice

The controller may update this notice from time to time to reflect changes in the law, in technology, or in the services offered.

The updated version will be made available through the portal.

Last updated: 03 October 2026`;
